import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Type, Image as ImageIcon, Megaphone, Youtube } from "lucide-react";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import api from "../lib/api";
import ImageUploader from "./ImageUploader";

/**
 * Visual editor for BlogPost.blocks[].
 * Each block: { type: "text"|"image"|"ad"|"video", ...fields }
 */

const TYPES = [
  { key: "text", label: "Texte", Icon: Type, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "image", label: "Image", Icon: ImageIcon, tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { key: "ad", label: "Publicité", Icon: Megaphone, tone: "bg-orange-50 text-orange-700 border-orange-200" },
  { key: "video", label: "Vidéo YouTube", Icon: Youtube, tone: "bg-red-50 text-red-700 border-red-200" },
];

function newBlock(type) {
  switch (type) {
    case "text":
      return { type: "text", text: "" };
    case "image":
      return { type: "image", src: "", caption: "", credit: "", alt: "" };
    case "ad":
      return { type: "ad", ad_id: "" };
    case "video":
      return { type: "video", url: "", caption: "" };
    default:
      return { type: "text", text: "" };
  }
}

export default function BlogBlocksEditor({ value, onChange }) {
  const blocks = Array.isArray(value) ? value : [];
  const [ads, setAds] = useState([]);

  useEffect(() => {
    // Fetch admin ads list for the ad-picker dropdown
    api.get("/admin/ads").then((r) => setAds(r.data || [])).catch(() => setAds([]));
  }, []);

  const update = (i, patch) => {
    const next = blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    onChange(next);
  };
  const add = (type) => onChange([...blocks, newBlock(type)]);
  const remove = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="blog-blocks-editor">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Blocs enrichis (optionnel — prime sur le Markdown ci-dessus)
        </label>
        <span className="text-[11px] text-muted-foreground">{blocks.length} bloc(s)</span>
      </div>

      {blocks.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-primary/20 p-6 text-center text-sm text-muted-foreground" data-testid="blocks-empty">
          Aucun bloc pour l'instant. L'article utilisera le Markdown ci-dessus. Ajoutez un bloc pour créer une mise en page enrichie style magazine.
        </div>
      )}

      {blocks.map((b, i) => {
        const meta = TYPES.find((t) => t.key === b.type) || TYPES[0];
        return (
          <div key={i} className="rounded-xl border border-primary/15 bg-white p-3" data-testid={`block-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border ${meta.tone}`}>
                <meta.Icon className="w-3 h-3" /> {meta.label}
              </span>
              <span className="text-xs text-muted-foreground ml-1">Bloc #{i + 1}</span>
              <div className="ml-auto flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30" title="Monter" data-testid={`block-up-${i}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === blocks.length - 1} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30" title="Descendre" data-testid={`block-down-${i}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Supprimer" data-testid={`block-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {b.type === "text" && (
              <div data-color-mode="light">
                <MDEditor
                  value={b.text || ""}
                  onChange={(v) => update(i, { text: v || "" })}
                  height={200}
                  preview="edit"
                  data-testid={`block-text-${i}`}
                />
              </div>
            )}

            {b.type === "image" && (
              <div className="space-y-2">
                <ImageUploader
                  value={b.src}
                  onChange={(v) => update(i, { src: v })}
                  label="Image du bloc"
                  testid={`block-image-src-${i}`}
                />
                <input
                  type="text"
                  placeholder="Légende (optionnel)"
                  value={b.caption || ""}
                  onChange={(e) => update(i, { caption: e.target.value })}
                  className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm"
                  data-testid={`block-image-caption-${i}`}
                />
                <input
                  type="text"
                  placeholder="Crédit photo (ex. Photo : Nom Prénom)"
                  value={b.credit || ""}
                  onChange={(e) => update(i, { credit: e.target.value })}
                  className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm"
                  data-testid={`block-image-credit-${i}`}
                />
                <input
                  type="text"
                  placeholder="Texte alternatif (accessibilité)"
                  value={b.alt || ""}
                  onChange={(e) => update(i, { alt: e.target.value })}
                  className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm"
                  data-testid={`block-image-alt-${i}`}
                />
              </div>
            )}

            {b.type === "ad" && (
              <div className="space-y-2">
                {ads.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    Aucune publicité disponible. Créez-en une dans l'onglet <strong>Publicités</strong>.
                  </p>
                ) : (
                  <select
                    value={b.ad_id || ""}
                    onChange={(e) => update(i, { ad_id: e.target.value })}
                    className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm bg-white"
                    data-testid={`block-ad-select-${i}`}
                  >
                    <option value="">— Sélectionner une publicité —</option>
                    {ads.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.title || "(sans titre)"} · {a.placement || "—"} {a.active ? "" : "(inactive)"}
                      </option>
                    ))}
                  </select>
                )}
                {b.ad_id && (
                  <p className="text-[11px] text-muted-foreground">
                    Aperçu affiché comme bannière au milieu de l'article.
                  </p>
                )}
              </div>
            )}

            {b.type === "video" && (
              <div className="space-y-2">
                <input
                  type="url"
                  placeholder="URL YouTube (https://www.youtube.com/watch?v=…)"
                  value={b.url || ""}
                  onChange={(e) => update(i, { url: e.target.value })}
                  className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm"
                  data-testid={`block-video-url-${i}`}
                />
                <input
                  type="text"
                  placeholder="Légende (optionnel)"
                  value={b.caption || ""}
                  onChange={(e) => update(i, { caption: e.target.value })}
                  className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm"
                  data-testid={`block-video-caption-${i}`}
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="rounded-xl bg-secondary/50 border border-primary/10 p-3">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Ajouter un bloc</p>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => add(t.key)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-primary/20 hover:border-primary text-xs font-medium transition"
              data-testid={`add-block-${t.key}`}
            >
              <Plus className="w-3 h-3" />
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
